// src/components/analytics/WorkOrderFlowDiagram.js
// Enhanced Work Order Flow Visualization with Advanced Features
// Version: 3.0 - Semantic UI & FlowSense Ready
// --- UPDATE (v3.1 · FlowSense AI Explainer + real-data pass)
//   • The widget endpoint answers { success, data: { nodes, links } }; only a bare { nodes, links }
//     was accepted, so the diagram showed "Insufficient data". Both shapes are read now.
//   • Filter fixes: "Isolate bottlenecks" / "Hide micro-flows" with no links produced NaN and
//     -Infinity thresholds that removed everything.
//   • The dimension selector only showed a toast. It now sends ?dimension= to the server and says
//     plainly when the server returned the status flow instead.
//   • "Maximum Bottleneck Link" was the largest flow — relabelled; smallest transition added.
//   • Node labels are readable in dark mode and flip sides at the real chart midpoint.
//   • Live: refetches on work order changes. Errors are shown instead of "Insufficient data".
//   • "Explain the work order journey" FlowSense AI explainer: where volume leaks out of the
//     pipeline (inflow vs outflow per stage), completion share, breach/risk volume.
// --- END UPDATE

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Text, Spinner, Flex, Heading, HStack, VStack, IconButton,
  Tooltip, Badge, Button, useColorModeValue, Switch,
  FormControl, FormLabel, Slider, SliderTrack, SliderFilledTrack,
  SliderThumb, useDisclosure, Modal, ModalOverlay, ModalContent, 
  ModalHeader, ModalBody, ModalCloseButton, SimpleGrid, Stat, 
  StatLabel, StatNumber, StatHelpText, Alert, AlertIcon, Progress, 
  Code, Divider, Collapse, useToast, Icon, Select
} from '@chakra-ui/react';
import { Sankey, Tooltip as RechartsTooltip, ResponsiveContainer, Layer, Rectangle } from 'recharts';
import {
  MdRefresh, MdDownload, MdSettings, MdFullscreen, MdFullscreenExit,
  MdFilterList, MdInfo, MdTimeline, MdArrowForward
} from 'react-icons/md';
import { FaChartArea, FaBrain, FaClock } from 'react-icons/fa';
import analyticsService from '../../services/analyticsService';
import FlowSenseAIExplainer from '../ai/FlowSenseAIExplainer'; // --- UPDATE (v3.1)
import useLiveRefresh from '../../hooks/useLiveRefresh';       // --- UPDATE (v3.1)

// --- UPDATE (v3.1): { nodes, links } from any of the shapes the analytics API returns
const asFlow = (res) => {
  for (const c of [res, res?.data, res?.data?.data]) {
    if (c && Array.isArray(c.nodes) && Array.isArray(c.links)) return c;
  }
  return null;
};
const nodeName = (nodes, ref) => (typeof ref === 'object' && ref ? ref.name : nodes?.[ref]?.name) ?? String(ref);
// --- END UPDATE

// ==================== SEMANTIC COLOR MAPPING ====================
const getSemanticColor = (nodeName) => {
  const name = nodeName?.toLowerCase() || '';
  // --- UPDATE (v3.2): stage names the real journey endpoint returns (checked before the generic rules —
  //   "Unassigned" contains "assigned", "AI rejected" contains "ai")
  if (name.includes('cancel') || name.includes('reject')) return '#a0aec0';
  if (name.includes('unassigned') || name.includes('waiting') || name.includes('hold') || name.includes('still open') || name.includes('not reviewed')) return '#ed8936';
  if (name.includes('finished') || name.includes('compliant') || name.includes('approved')) return '#48bb78';
  if (name.includes('started') || name === 'created') return '#3182ce';
  if (name.includes('manual')) return '#718096';
  // --- END UPDATE
  if (name.includes('new')) return '#3182ce'; // blue.500
  if (name.includes('triage')) return '#805ad5'; // purple.500
  if (name.includes('pending')) return '#ed8936'; // orange.400
  if (name.includes('assigned') || name.includes('dispatch')) return '#38b2ac'; // teal.400
  if (name.includes('progress')) return '#ecc94b'; // yellow.400
  if (name.includes('complet')) return '#48bb78'; // green.400
  if (name.includes('close')) return '#718096'; // gray.500
  if (name.includes('breach') || name.includes('risk')) return '#e53e3e'; // red.500
  if (name.includes('ai')) return '#00b5d8'; // cyan.500
  return '#a0aec0'; // default gray.400
};

// ==================== HELPER FUNCTIONS ====================
const calculateFlowMetrics = (data) => {
  if (!data || !data.links) return null;

  const totalFlow = data.links.reduce((sum, link) => sum + (Number(link.value) || 0), 0);
  const avgFlow = totalFlow / (data.links.length || 1);
  const maxFlow = data.links.length ? Math.max(...data.links.map(l => Number(l.value) || 0)) : 0; // --- UPDATE (v3.1)
  const minFlow = data.links.length ? Math.min(...data.links.map(l => Number(l.value) || 0)) : 0; // --- UPDATE (v3.1): was Infinity

  return {
    totalFlow,
    avgFlow: avgFlow.toFixed(1),
    maxFlow,
    minFlow,
    flowCount: data.links.length,
    nodeCount: data.nodes?.length || 0
  };
};

const filterFlowData = (data, filters) => {
  if (!data || !data.links) return data;

  const { minFlow, showBottlenecks, hideSmallFlows } = filters;

  let filteredLinks = data.links.filter(link => {
    if (link.source >= link.target) return false; // Remove circular loops
    if (link.value < minFlow) return false;
    return true;
  });

  // --- UPDATE (v3.1): guard empty lists (NaN / -Infinity thresholds removed every link)
  if (showBottlenecks && filteredLinks.length) {
    const avgFlow = filteredLinks.reduce((sum, l) => sum + l.value, 0) / filteredLinks.length;
    filteredLinks = filteredLinks.filter(link => link.value < avgFlow * 0.5);
  }

  if (hideSmallFlows && filteredLinks.length) {
    const maxFlow = Math.max(...filteredLinks.map(l => l.value));
    filteredLinks = filteredLinks.filter(link => link.value >= maxFlow * 0.1);
  }
  // --- END UPDATE

  return { nodes: data.nodes, links: filteredLinks };
};

// ==================== CUSTOM RECHARTS COMPONENTS ====================

const CustomSankeyNode = ({ x, y, width, height, index, payload, labelColor = '#4A5568', splitX = 400 }) => {
  const isOut = x + width > splitX; // --- UPDATE (v3.1): real chart midpoint, not a fixed 400 px
  const fillColor = getSemanticColor(payload.name);
  
  return (
    <Layer key={`CustomNode${index}`}>
      <Rectangle
        x={x} y={y} width={width} height={height}
        fill={fillColor}
        fillOpacity="0.9"
        rx={4} ry={4}
      />
      <text
        textAnchor={isOut ? 'end' : 'start'}
        x={isOut ? x - 8 : x + width + 8}
        y={y + height / 2}
        dy={4}
        fontSize="12px"
        fontWeight="700"
        fill={labelColor}
        style={{ pointerEvents: 'none' }}
      >
        {`${payload.name} (${payload.value})`}
      </text>
    </Layer>
  );
};

const CustomSankeyLink = ({ sourceX, targetX, sourceY, targetY, sourceControlX, targetControlX, linkWidth, source, target, linkOpacity }) => {
  const sourceColor = getSemanticColor(source?.name);
  
  return (
    <path
      d={`
        M${sourceX},${sourceY}
        C${sourceControlX},${sourceY} ${targetControlX},${targetY} ${targetX},${targetY}
      `}
      fill="none"
      stroke={sourceColor}
      strokeWidth={Math.max(linkWidth, 1)}
      strokeOpacity={linkOpacity}
      style={{ transition: 'all 0.3s ease' }}
      onMouseEnter={(e) => { e.target.style.strokeOpacity = 0.8; }}
      onMouseLeave={(e) => { e.target.style.strokeOpacity = linkOpacity; }}
    />
  );
};

const CustomTooltip = ({ active, payload }) => {
  const tooltipBg = useColorModeValue('white', 'gray.800');
  const tooltipBorder = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.700', 'gray.200');

  if (!active || !payload || !payload.length) return null;

  const data = payload[0];
  const isNode = data.name !== undefined;
  const isLink = data.payload?.source !== undefined;

  return (
    <Box bg={tooltipBg} borderWidth="1px" borderColor={tooltipBorder} borderRadius="lg" p={3} boxShadow="xl" maxW="250px">
      {isNode && (
        <VStack align="stretch" spacing={2}>
          <HStack>
            <Box w={3} h={3} borderRadius="full" bg={getSemanticColor(data.name)} />
            <Heading size="xs" color={textColor}>{data.name}</Heading>
          </HStack>
          <Divider />
          <HStack justify="space-between">
            <Text fontSize="xs" color="gray.500">Total Flow Volume:</Text>
            <Badge colorScheme="blue" fontSize="xs">{data.value}</Badge>
          </HStack>
        </VStack>
      )}

      {isLink && (
        <VStack align="stretch" spacing={2}>
          <Heading size="xs" color={textColor}>Flow Transition</Heading>
          <Divider />
          <HStack spacing={2} fontSize="sm">
            <Text color={textColor} fontWeight="bold">{data.payload.source.name}</Text>
            <Icon as={MdArrowForward} color={getSemanticColor(data.payload.source.name)} />
            <Text color={textColor} fontWeight="bold">{data.payload.target.name}</Text>
          </HStack>
          <HStack justify="space-between">
            <Text fontSize="xs" color="gray.500">Volume Transferred:</Text>
            <Badge colorScheme="purple" fontSize="xs">{data.payload.value}</Badge>
          </HStack>
        </VStack>
      )}
    </Box>
  );
};

// ==================== MAIN COMPONENT ====================

const WorkOrderFlowDiagram = ({ 
  height = 500,
  enableFilters = true,
  enableStats = true,
  autoRefresh = false,
  refreshInterval = 60000,
}) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const headingColor = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  const statBg = useColorModeValue('gray.50', 'gray.700');
  const filterBg = useColorModeValue('gray.50', 'gray.700');
  const nodeLabelColor = useColorModeValue('#4A5568', '#E2E8F0'); // --- UPDATE (v3.1)
  const iconBg = useColorModeValue('blue.50', 'whiteAlpha.100');  // --- UPDATE (v3.1)
  const [dimensionNote, setDimensionNote] = useState(null);       // --- UPDATE (v3.1)

  const [rawData, setRawData] = useState({ nodes: [], links: [] });
  const [processedData, setProcessedData] = useState({ nodes: [], links: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showFilters, setShowFilters] = useState(enableFilters);
  const [flowDimension, setFlowDimension] = useState('status'); // FlowSense Connectivity Hook
  
  const [filters, setFilters] = useState({
    minFlow: 0,
    showBottlenecks: false,
    hideSmallFlows: false,
  });

  const [chartConfig, setChartConfig] = useState({
    nodeWidth: 15,
    nodePadding: 45,
    linkOpacity: 0.35,
  });

  const [performanceMetrics, setPerformanceMetrics] = useState({
    renderTime: 0,
    dataProcessingTime: 0,
    lastUpdate: null
  });

  const refreshTimerRef = useRef(null);
  const containerRef = useRef(null);

  const toast = useToast();
  const { isOpen: isSettingsOpen, onOpen: onSettingsOpen, onClose: onSettingsClose } = useDisclosure();
  const { isOpen: isStatsOpen, onOpen: onStatsOpen, onClose: onStatsClose } = useDisclosure();

  const fetchData = useCallback(async (showToast = false) => {
    const startTime = performance.now();
    try {
      setIsLoading(true);
      setError(null);

      // --- UPDATE (v3.1): dimension is sent; response shape normalised
      const response = await analyticsService.getWorkOrderFlowData(flowDimension === 'status' ? undefined : { dimension: flowDimension });
      const result = asFlow(response);
      const dataProcessingTime = performance.now() - startTime;

      if (result) {
        const served = result.dimension || response?.dimension || response?.data?.dimension || 'status';
        setDimensionNote(flowDimension !== 'status' && served !== flowDimension
          ? `The server returned the ${served} flow — the ${flowDimension === 'sla' ? 'SLA split' : 'AI vs manual split'} needs backend support for ?dimension=${flowDimension}.`
          : null);
        setRawData(result);
        // --- END UPDATE
        setLastRefresh(new Date());
        setPerformanceMetrics(prev => ({
          ...prev,
          dataProcessingTime: dataProcessingTime.toFixed(2),
          lastUpdate: new Date()
        }));

        if (showToast) {
          toast({ title: 'Flow Data Updated', status: 'success', duration: 2000 });
        }
      } else {
        throw new Error('Invalid data structure received');
      }
    } catch (err) {
      setError(err.message || 'Failed to load flow data');
      toast({ title: 'Error Loading Flow', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setIsLoading(false);
    }
  }, [toast, flowDimension]); // --- UPDATE (v3.1)

  useLiveRefresh(['work_order_events'], () => fetchData(false), { delay: 6000 }); // --- UPDATE (v3.1)

  useEffect(() => {
    const startTime = performance.now();
    const filtered = filterFlowData(rawData, filters);
    setProcessedData(filtered);
    setPerformanceMetrics(prev => ({ ...prev, renderTime: (performance.now() - startTime).toFixed(2) }));
  }, [rawData, filters]);

  useEffect(() => { fetchData(false); }, [fetchData]);

  useEffect(() => {
    if (autoRefresh && refreshInterval > 0) {
      refreshTimerRef.current = setInterval(() => fetchData(false), refreshInterval);
      return () => clearInterval(refreshTimerRef.current);
    }
  }, [autoRefresh, refreshInterval, fetchData]);

  const flowMetrics = useMemo(() => calculateFlowMetrics(processedData), [processedData]);

  // --- UPDATE (v3.1): FlowSense rule findings from the real graph (unfiltered)
  const flowExplainer = useMemo(() => {
    const nodes = rawData?.nodes || []; const links = rawData?.links || [];
    const findings = [];
    if (!links.length) return { findings, facts: {} };
    const io = {};
    links.forEach(l => {
      const s = nodeName(nodes, l.source); const t = nodeName(nodes, l.target); const v = Number(l.value) || 0;
      io[s] = io[s] || { in: 0, out: 0 }; io[t] = io[t] || { in: 0, out: 0 };
      io[s].out += v; io[t].in += v;
    });
    const stages = Object.entries(io).map(([name, x]) => ({ name, inflow: x.in, outflow: x.out, held: x.in - x.out }));
    const isEnd = (n) => /complet|close|cancel|reject|resolv|finished/i.test(n);
    const leaks = stages.filter(s => s.inflow > 0 && !isEnd(s.name) && s.held > 0).sort((a, b) => b.held - a.held);
    const entry = stages.filter(s => s.inflow === 0).reduce((sum, s) => sum + s.outflow, 0);
    const done = stages.filter(s => isEnd(s.name) && s.outflow === 0).reduce((sum, s) => sum + s.inflow, 0); // --- UPDATE (v3.2): leaves only — no double count of Completed → Closed
    const risk = stages.filter(s => /breach|risk|overdue|escalat/i.test(s.name)).reduce((sum, s) => sum + s.inflow, 0);
    if (leaks[0]) findings.push({ severity: leaks[0].held / Math.max(leaks[0].inflow, 1) >= 0.5 ? 'high' : 'medium', text: `${leaks[0].held} work orders are sitting in "${leaks[0].name}" (${leaks[0].inflow} in, ${leaks[0].outflow} out).` });
    leaks.slice(1, 3).forEach(s => findings.push({ severity: 'info', text: `"${s.name}" holds ${s.held} (${s.inflow} in, ${s.outflow} out).` }));
    if (entry > 0) findings.push({ severity: done / entry >= 0.7 ? 'ok' : 'medium', text: `${done} of ${entry} work orders reached a finished stage — completed, closed or cancelled (${((done / entry) * 100).toFixed(0)}%).` });
    if (risk > 0) findings.push({ severity: 'high', text: `${risk} work orders flowed into breach / risk / escalation stages.` });
    return {
      findings,
      facts: { definitions: rawData?.definitions || null, range: rawData?.range || null, dimension_requested: flowDimension, dimension_note: dimensionNote, stages, top_links: [...links].sort((a, b) => b.value - a.value).slice(0, 12).map(l => ({ from: nodeName(nodes, l.source), to: nodeName(nodes, l.target), value: l.value })) },
    };
  }, [rawData, flowDimension, dimensionNote]);
  // --- END UPDATE
  const safeHeight = typeof height === 'number' ? `${height}px` : (height || '500px');

  return (
    <Box ref={containerRef} bg={cardBg} borderRadius="lg" borderWidth="1px" borderColor={borderColor} p={5} boxShadow="sm" position="relative">
      
      {/* Header Area */}
      <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={3}>
        <HStack spacing={3}>
          <Box p={2} bg={iconBg} borderRadius="lg">
            <Icon as={FaChartArea} boxSize={5} color="blue.500" />
          </Box>
          <VStack align="flex-start" spacing={0}>
            <Heading size="md" color={headingColor}>Work Order Journey</Heading>
            <HStack>
              {lastRefresh && <Text fontSize="xs" color={mutedColor}>Updated: {lastRefresh.toLocaleTimeString()}</Text>}
              {processedData.links.length > 0 && <Badge colorScheme="blue" variant="subtle" fontSize="2xs">{processedData.links.length} paths</Badge>}
            </HStack>
          </VStack>
        </HStack>

        <HStack spacing={2} flexWrap="wrap">
          {/* FLOWSENSE INTEGRATION HOOK: Dimension Selector */}
          <Select 
            size="sm" 
            w="160px" 
            borderRadius="md" 
            value={flowDimension} 
            onChange={(e) => setFlowDimension(e.target.value)} // --- UPDATE (v3.1): refetches with ?dimension=
          >
            <option value="status">Pipeline Status Flow</option>
            <option value="sla">SLA Compliance Splits</option>
            <option value="ai">AI vs Manual Routing</option>
          </Select>

          {enableFilters && (
            <Tooltip label="Flow Filters">
              <IconButton icon={<MdFilterList />} size="sm" variant={showFilters ? 'solid' : 'outline'} colorScheme={showFilters ? 'blue' : 'gray'} onClick={() => setShowFilters(!showFilters)} />
            </Tooltip>
          )}
          {enableStats && (
            <Tooltip label="Flow Statistics">
              <IconButton icon={<MdInfo />} size="sm" variant="outline" onClick={onStatsOpen} />
            </Tooltip>
          )}
          <Tooltip label="Settings">
            <IconButton icon={<MdSettings />} size="sm" variant="outline" onClick={onSettingsOpen} />
          </Tooltip>
          <Tooltip label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
            <IconButton icon={isFullscreen ? <MdFullscreenExit /> : <MdFullscreen />} size="sm" variant="outline" onClick={() => {
              if (!document.fullscreenElement) { containerRef.current.requestFullscreen(); setIsFullscreen(true); }
              else { document.exitFullscreen(); setIsFullscreen(false); }
            }} />
          </Tooltip>
        </HStack>
      </Flex>

      {/* Filters Collapse */}
      <Collapse in={showFilters} animateOpacity>
        <Box bg={filterBg} p={4} borderRadius="md" mb={4} borderWidth="1px" borderColor={borderColor}>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={5} alignItems="center">
            <FormControl>
              <FormLabel fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase">Min Flow Threshold: {filters.minFlow}</FormLabel>
              <Slider value={filters.minFlow} onChange={(val) => setFilters(prev => ({ ...prev, minFlow: val }))} min={0} max={100} step={5} colorScheme="blue">
                <SliderTrack><SliderFilledTrack /></SliderTrack>
                <SliderThumb boxSize={4} />
              </Slider>
            </FormControl>
            <FormControl display="flex" alignItems="center" justifyContent={{md: "center"}}>
              <FormLabel fontSize="sm" mb={0} mr={2}>Isolate Bottlenecks</FormLabel>
              <Switch isChecked={filters.showBottlenecks} onChange={(e) => setFilters(prev => ({ ...prev, showBottlenecks: e.target.checked }))} colorScheme="red" />
            </FormControl>
            <FormControl display="flex" alignItems="center" justifyContent={{md: "flex-end"}}>
              <FormLabel fontSize="sm" mb={0} mr={2}>Hide Micro-Flows</FormLabel>
              <Switch isChecked={filters.hideSmallFlows} onChange={(e) => setFilters(prev => ({ ...prev, hideSmallFlows: e.target.checked }))} colorScheme="blue" />
            </FormControl>
          </SimpleGrid>
        </Box>
      </Collapse>

      {/* Chart Area */}
      {isLoading ? (
        <Flex height={safeHeight} align="center" justify="center" direction="column">
          <Spinner size="xl" color="blue.500" thickness="3px" mb={4} />
          <Text color={mutedColor} fontWeight="bold">Mapping Work Order Pipeline...</Text>
        </Flex>
      ) : error || !processedData.nodes.length ? (
        <Flex height={safeHeight} align="center" justify="center" direction="column">
          <Icon as={MdTimeline} boxSize={12} color="gray.300" mb={3} />
          <Text color={mutedColor} fontSize="md">{error ? 'Could not load the work order flow.' : 'Insufficient data to map flow pipeline.'}</Text>
          {/* --- UPDATE (v3.1) */}
          {error && <Text color="red.400" fontSize="sm" mt={1}>{error}</Text>}
          {error && <Button size="sm" mt={3} leftIcon={<MdRefresh />} onClick={() => fetchData(true)}>Retry</Button>}
        </Flex>
      ) : (
        <Box h={safeHeight} minH={safeHeight} width="100%" position="relative">
          <ResponsiveContainer width="100%" height="100%">
            <Sankey
              data={processedData}
              margin={{ top: 20, right: 100, bottom: 20, left: 20 }}
              nodeWidth={chartConfig.nodeWidth}
              nodePadding={chartConfig.nodePadding}
              node={<CustomSankeyNode labelColor={nodeLabelColor} splitX={(containerRef.current?.offsetWidth || 800) / 2} />}
              link={(props) => <CustomSankeyLink {...props} linkOpacity={chartConfig.linkOpacity} />}
            >
              <RechartsTooltip content={<CustomTooltip />} />
            </Sankey>
          </ResponsiveContainer>
        </Box>
      )}

      {/* --- UPDATE (v3.1) */}
      {dimensionNote && (
        <Alert status="info" borderRadius="md" mt={3} fontSize="sm"><AlertIcon />{dimensionNote}</Alert>
      )}
      {!isLoading && rawData.links.length > 0 && (
        <Box mt={4}>
          <FlowSenseAIExplainer
            module="work_order_flow"
            title="Explain the work order journey"
            subtitle="Where work flows, stalls and finishes"
            facts={flowExplainer.facts}
            findings={flowExplainer.findings}
            compact
            questions={['Where is work getting stuck?', 'How do I raise the completion rate?']}
          />
        </Box>
      )}
      {/* --- END UPDATE */}

      {/* Stats Modal */}
      <Modal isOpen={isStatsOpen} onClose={onStatsClose} size="lg">
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent borderRadius="xl">
          <ModalHeader>Pipeline Intelligence</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <SimpleGrid columns={2} spacing={4} mb={6}>
              <Box bg={statBg} p={4} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
                <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase">Total Journey Nodes</Text>
                <Text fontSize="2xl" fontWeight="900" color="blue.500">{flowMetrics?.nodeCount || 0}</Text>
              </Box>
              <Box bg={statBg} p={4} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
                <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase">Total Volume Moved</Text>
                <Text fontSize="2xl" fontWeight="900" color="purple.500">{flowMetrics?.totalFlow || 0}</Text>
              </Box>
            </SimpleGrid>
            <Divider mb={4} />
            <Text fontSize="xs" color="gray.500" fontWeight="bold" mb={2}>FLOWSENSE SYSTEM METRICS</Text>
            <VStack align="stretch" spacing={2} fontSize="sm">
              <HStack justify="space-between"><Text>Avg Orders Per Transition:</Text><Badge>{flowMetrics?.avgFlow || 0}</Badge></HStack>
              <HStack justify="space-between"><Text>Largest transition:</Text><Badge colorScheme="purple">{flowMetrics?.maxFlow || 0}</Badge></HStack>
              <HStack justify="space-between"><Text>Smallest transition:</Text><Badge colorScheme="orange">{flowMetrics?.flowCount ? flowMetrics.minFlow : '—'}</Badge></HStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Settings Modal */}
      <Modal isOpen={isSettingsOpen} onClose={onSettingsClose}>
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent borderRadius="xl">
          <ModalHeader>Visual Configuration</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={6} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm" color="gray.500" fontWeight="bold">Link Opacity ({(chartConfig.linkOpacity * 100).toFixed(0)}%)</FormLabel>
                <Slider value={chartConfig.linkOpacity * 100} onChange={(val) => setChartConfig(prev => ({ ...prev, linkOpacity: val / 100 }))} min={10} max={100} step={5}>
                  <SliderTrack><SliderFilledTrack bg="blue.400" /></SliderTrack>
                  <SliderThumb />
                </Slider>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" color="gray.500" fontWeight="bold">Node Spacing ({chartConfig.nodePadding}px)</FormLabel>
                <Slider value={chartConfig.nodePadding} onChange={(val) => setChartConfig(prev => ({ ...prev, nodePadding: val }))} min={10} max={80} step={5}>
                  <SliderTrack><SliderFilledTrack bg="purple.400" /></SliderTrack>
                  <SliderThumb />
                </Slider>
              </FormControl>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

    </Box>
  );
};

export default WorkOrderFlowDiagram;